import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Mail, CheckCircle, Loader2 } from "lucide-react";

export default function EmailConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get("email");
  const [resending, setResending] = useState(false);

  const handleResendEmail = async () => {
    if (!email) {
      toast({
        title: "Fehler",
        description: "E-Mail-Adresse nicht gefunden.",
        variant: "destructive",
      });
      return;
    }

    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      toast({
        title: "E-Mail erneut gesendet",
        description: "Bitte überprüfen Sie Ihr Postfach.",
      });
    } catch (err: any) {
      toast({
        title: "Fehler beim Senden",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle>E-Mail-Bestätigung erforderlich</CardTitle>
          <CardDescription>
            Wir haben dir eine Bestätigungs-E-Mail gesendet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            {email && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">E-Mail gesendet an:</p>
                <p className="font-medium">{email}</p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Überprüfe dein Postfach</p>
                  <p className="text-sm text-muted-foreground">
                    Klicke auf den Bestätigungslink in der E-Mail, um deinen Account zu aktivieren.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Prüfe deinen Spam-Ordner</p>
                  <p className="text-sm text-muted-foreground">
                    Manchmal landen unsere E-Mails im Spam-Ordner. Falls du keine E-Mail erhalten hast, schaue dort nach.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Link gültig für 24 Stunden</p>
                  <p className="text-sm text-muted-foreground">
                    Der Bestätigungslink ist 24 Stunden lang gültig. Danach kannst du eine neue E-Mail anfordern.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 pt-4 border-t">
            <p className="text-sm text-muted-foreground text-center mb-3">
              Keine E-Mail erhalten?
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleResendEmail}
              disabled={resending || !email}
            >
              {resending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                "Bestätigungs-E-Mail erneut senden"
              )}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate("/auth")}
            >
              Zurück zur Anmeldung
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
